import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Clock,
  TrendingDown,
  Megaphone,
  FileVideo,
  Send,
  Sparkles,
  Compass,
  Building2,
  Briefcase,
  Heart,
  ShoppingBag,
  BarChart3,
  ShieldCheck,
  PlayCircle,
  CheckCircle2,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import HeroTemplateEnts from '../../components/sections/HeroTemplateEnts';
import CampagneParcoursIllustration from '../../components/sections/CampagneParcoursIllustration';
import CandidateTestimonial from '../../components/sections/CandidateTestimonial';
import WhitepaperModal from '../../components/whitepaper/WhitepaperModal';
import { useWhitepaperPrompt } from '../../hooks/useWhitepaperPrompt';
import { ATTIRER_ONBOARDER_TALENTS } from '../../lib/whitepapers';
import { WebPageSchema } from '../../lib/utils/schema-utils';

// — Typography system, aligned on home-page.tsx ————————————————————————
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class =
  'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass =
  'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass = 'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Section 2 — Le constat : 3 cartes chiffrées ———————————————————————
const painPoints = [
  {
    icon: TrendingDown,
    metric: 'jusqu’à ×4',
    title: 'Coût d’acquisition par candidature',
    description:
      "Quand vous sourcez dans l’urgence, sur des plateformes saturées et avec des marques employeurs invisibles, le coût d’une candidature qualifiée peut être multiplié jusqu’à 4 par rapport à une approche en vivier construit en amont.",
  },
  {
    icon: Clock,
    metric: '60 à 90 jours',
    title: 'Pour combler un poste critique',
    description:
      "Sourcing, premiers entretiens, allers-retours managers, négociations, préavis : la moyenne marché reste de deux à trois mois pour un poste cadre. Et chaque jour de retard coûte en chiffre d’affaires non capté ou en équipe en surcharge.",
  },
  {
    icon: AlertTriangle,
    metric: '1 sur 5',
    title: 'Candidats qui se rétractent après l’offre',
    description:
      "Sans relation préalable et sans visibilité sur la culture de l’entreprise, les candidats acceptent par défaut puis comparent. Une offre concurrente, une promesse vague, un doute sur la culture — et le « oui » du vendredi devient le « non » du lundi.",
  },
];

// — Section 3 — La méthode : timeline 5 temps ————————————————————————
const methodSteps = [
  {
    icon: Compass,
    title: 'Audit de votre marque employeur',
    description:
      "Nous cartographions votre culture, vos valeurs vécues, votre quotidien réel et les codes qui vous rendent uniques. Pas du marketing : de la matière vraie, recueillie auprès des dirigeants, des managers et des collaborateurs.",
  },
  {
    icon: FileVideo,
    title: 'Production de contenus',
    description:
      "Vidéos terrain, témoignages collaborateurs, présentations dirigeants, posts métier. Nous transformons votre matière en contenus qui parlent aux candidats — pas aux recruteurs. Authentiques, partageables, durables.",
  },
  {
    icon: Send,
    title: 'Diffusion ciblée et construction du vivier',
    description:
      "Nous diffusons ces contenus aux profils qui correspondent à vos exigences (LinkedIn, réseaux pros, sourcing IA). Chaque interaction enrichit votre vivier : un actif qui vous appartient et qui se valorise dans le temps.",
  },
  {
    icon: Megaphone,
    title: 'Acculturation continue',
    description:
      "Les candidats du vivier reçoivent vos contenus dans la durée. Ils s’imprègnent de votre culture, comprennent vos enjeux, se familiarisent avec vos équipes. Avant même d’être contactés sur un poste, ils vous connaissent déjà.",
  },
  {
    icon: Sparkles,
    title: 'Activation Parcours Découverte',
    description:
      "Quand un poste s’ouvre, nous activons les candidats du vivier les plus engagés ET les plus alignés sur vos exigences. Ils traversent leur Parcours Découverte. Ils signent en connaissance de cause. Ils tiennent.",
  },
  {
    icon: ShieldCheck,
    title: 'Préparer l’intégration, pas juste l’embauche',
    description:
      "L’intégration commence avant le jour 1. Onboarding personnalisé dès la signature, points de feedback RH et managers, objectifs suivis semaine par semaine : les nouvelles recrues prennent leur poste plus vite, et le turnover des 90 premiers jours chute.",
  },
];

// — Section méthode étape 6 (bullet checklist) ————————————————————————
const integrationBullets = [
  'Onboarding personnalisé dès la signature, sans attendre le jour 1',
  'Points de feedback structurés côté RH et côté manager direct',
  'Objectifs prise de poste suivis semaine par semaine sur les 90 premiers jours',
];

// — Section 4 — Pour qui ————————————————————————————————————————————
const fitProfiles = [
  {
    icon: Building2,
    title: 'Vous recrutez en volume sur des fonctions récurrentes',
    description:
      "Plus de 15 postes par an sur du commercial, du terrain, du retail, de la restauration, du médical, du BTP. La campagne devient votre canal d’embauche permanent.",
  },
  {
    icon: Briefcase,
    title: 'Vous ouvrez un site, un magasin, une équipe',
    description:
      "Recruter en volume sans casser la qualité. La campagne pré-construit le vivier des semaines avant l’ouverture, ce qui fluidifie le ramp-up.",
  },
  {
    icon: AlertTriangle,
    title: 'Vous subissez le marché sur des profils tendus',
    description:
      "IT, ingénieurs, experts métier, profils rares. La campagne vous remet en position d’attirer plutôt que de chercher.",
  },
  {
    icon: Heart,
    title: 'Votre marque employeur est sous-exploitée',
    description:
      "Vous avez une vraie histoire, des collaborateurs engagés, une culture forte — mais ça ne se voit pas dehors. La campagne capitalise sur ce qui existe déjà chez vous.",
  },
];

// — Section 5 — Double dividende ————————————————————————————————————
const dividendStats = [
  {
    icon: ShoppingBag,
    metric: '64 %',
    text:
      "des candidats qui ont vécu une mauvaise expérience de recrutement arrêtent d’acheter chez la marque concernée. Source : Talent Board.",
  },
  {
    icon: Heart,
    metric: '88 %',
    text:
      "des candidats avec une bonne expérience deviennent acheteurs des produits ou services de l’entreprise — y compris s’ils n’ont pas été retenus. Source : Talent Board.",
  },
  {
    icon: BarChart3,
    metric: '91 % vs 26 %',
    text:
      "des employeurs admettent que l’expérience candidat impacte leurs ventes. Mais seulement 26 % la mesurent. Le gap est votre opportunité.",
  },
];

// — Section 6 — Chiffres avant / après ————————————————————————————————
const beforeAfterRows = [
  {
    label: 'Délai pour combler un poste',
    before: '60 à 90 jours',
    after: 'moins de 30 jours',
  },
  {
    label: 'Coût d’acquisition par candidature qualifiée',
    before: '× 1 (référence marché)',
    after: 'jusqu’à divisé par 4',
  },
  {
    label: 'Taux de retrait après offre',
    before: '~ 20 %',
    after: 'moins de 5 %',
  },
  {
    label: 'Candidats actifs en vivier',
    before: '0',
    after: '200 à 800',
  },
  {
    label: 'Capital marque employeur',
    before: 'à la merci des avis Glassdoor',
    after: 'scénarisé, mesuré, capitalisé',
  },
];

export default function CampagnesPage() {
  const navigate = useNavigate();
  const whitepaper = useWhitepaperPrompt({ slug: ATTIRER_ONBOARDER_TALENTS.slug });

  return (
    <Layout
      title="Campagnes de recrutement, vivier et marque employeur | recruteurs.io"
      description="Campagnes de recrutement à Marseille, Aix et PACA : construction de vivier, contenus marque employeur, acculturation continue, activation Parcours Découverte. Le recrutement piloté comme un canal d’acquisition."
      keywords="campagne de recrutement, vivier de candidats, marque employeur, recrutement retail, recrutement multi-postes, recrutement réseau, sourcing continu, acculturation candidat, parcours découverte"
    >
      <WebPageSchema
        page={{
          title: 'Campagnes de recrutement, vivier et marque employeur | recruteurs.io',
          description:
            "recruteurs.io structure des campagnes de recrutement continues : audit marque employeur, production de contenus, construction de vivier, acculturation des candidats, activation Parcours Découverte. Pour les entreprises qui recrutent en volume ou en récurrence.",
          url: 'https://recruteurs.io/entreprise/campagnes',
          lastReviewed: '2026-05-09',
        }}
      />

      {/* HERO */}
      <HeroTemplateEnts
        page="campagnes"
        title="Arrêtez de chercher quand vous avez besoin. Puisez dans votre vivier."
        eyebrow="Campagne de recrutement · Marque employeur · Vivier · Marseille · Aix · PACA"
        description="Une campagne de recrutement, ce n’est plus une annonce qu’on diffuse en panique. C’est un vivier de candidats qu’on construit, qu’on nourrit, qu’on acculture à votre culture — pour activer les plus engagés avec un Parcours Découverte ciblé. recruteurs.io est la seule agence à Marseille qui combine chasse de tête senior, IA appliquée au sourcing et Plateforme Parcours Découverte sous le même toit."
        customRight={<CampagneParcoursIllustration />}
        breadcrumbs={[
          { name: 'Entreprise', href: '/entreprise/recrutement' },
          { name: 'Campagnes', href: '/entreprise/campagnes' },
        ]}
        cta1={{
          label: 'Lancer une campagne',
          onClick: () => navigate('/reservation'),
        }}
        cta2={{
          label: 'Voir le Parcours Découverte',
          onClick: () => navigate('/decouvrir'),
        }}
      />

      {/* SECTION 2 — Le constat */}
      <SectionBackground page="campagnes" section="campagnes-pain" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le problème</span>

            <h2 className={h2Class}>
              Vous recrutez dans l’urgence.{' '}
              <span className={gradientText}>Et l’urgence vous coûte cher.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Quand vous lancez une annonce parce qu’un poste vient de s’ouvrir, vous arrivez
              trop tard : les bons candidats sont déjà ailleurs, ceux qui répondent ne vous
              connaissent pas, et vous payez le sourcing au prix fort sur des plateformes saturées.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {painPoints.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-6 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-7 h-7" />
                    </div>
                    <p className="text-3xl font-bold text-[#30475e] tracking-tight">
                      {item.metric}
                    </p>
                  </div>

                  <h3 className={h3Class}>{item.title}</h3>
                  <p className={bodyClass}>{item.description}</p>
                </motion.div>
              );
            })}
          </div>

          <p className="text-sm text-neutral-400 text-center mt-8 max-w-2xl mx-auto">
            Ordres de grandeur du marché. Sources&nbsp;: LinkedIn Talent Trends,
            Glassdoor, études sectorielles 2024-2025.
          </p>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — La méthode */}
      <SectionBackground page="campagnes" section="campagnes-method" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Notre méthode en 6 temps</span>

            <h2 className={h2Class}>
              La campagne recruteurs.io transforme votre marque employeur en{' '}
              <span className={gradientText}>machine à embauches durables.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Six étapes qui s’enchaînent dans la durée. Vous ne lancez plus
              une campagne&nbsp;: vous installez un canal d’acquisition de talents
              permanent — du premier contact à l’intégration au 90&shy;e jour.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {methodSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  className="group rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-8 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>
                    <Icon className="w-6 h-6 text-neutral-300 group-hover:text-[#d03484] transition-colors duration-300" />
                  </div>

                  <h3 className={h3Class}>{step.title}</h3>
                  <p className={bodyClass}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Focus intégration — bloc détaillé sur l'étape 6 */}
          <div className="mt-12 rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
              <div>
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-3">
                  Focus étape 06 · Intégration
                </p>
                <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[#30475e] leading-tight">
                  L’intégration commence{' '}
                  <span className={gradientText}>avant le jour 1.</span>
                </h3>
                <p className={`${bodyClass} mt-4`}>
                  Un recrutement réussi se joue dans les 90 premiers jours. Notre
                  protocole d’intégration, calé dès la signature, accélère la
                  montée en compétence et réduit drastiquement le turnover précoce.
                </p>
              </div>

              <ul className="space-y-4">
                {integrationBullets.map((line) => (
                  <li
                    key={line}
                    className="flex items-start gap-3 rounded-2xl bg-[#fbfaf8] px-5 py-4 ring-1 ring-neutral-200"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#d03484] flex-shrink-0 mt-0.5" />
                    <span className="text-base text-neutral-800 leading-snug">
                      {line}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Pour qui */}
      <SectionBackground page="campagnes" section="campagnes-fit" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Pour qui</span>

            <h2 className={h2Class}>
              La campagne recruteurs.io est faite pour vous{' '}
              <span className={gradientText}>si&hellip;</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {fitProfiles.map((profile, index) => {
              const Icon = profile.icon;
              return (
                <motion.div
                  key={profile.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{profile.title}</h3>
                  <p className={bodyClass}>{profile.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Double dividende */}
      <SectionBackground page="campagnes" section="campagnes-dividend" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Le bénéfice qui paie deux fois</span>

            <h2 className={h2Class}>
              Votre vivier travaille pour vous,{' '}
              <span className={gradientText}>même quand vous ne recrutez pas.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Chaque candidat de votre vivier — qu’il soit retenu, refusé, ou simplement
              spectateur — repart avec votre histoire. Celle que <strong>vous</strong> avez
              choisi de raconter. Pas une rumeur de couloir, pas un avis Glassdoor frustré.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {dividendStats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={stat.metric}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm"
                >
                  <div className="mb-5 flex items-center justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-6 h-6" />
                    </div>
                    <p className="text-3xl font-bold text-[#30475e] tracking-tight">
                      {stat.metric}
                    </p>
                  </div>
                  <p className={bodyClass}>{stat.text}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 max-w-4xl mx-auto">
            <p className={`${bodyClass} text-center`}>
              Une campagne recruteurs.io, c’est un investissement marque employeur qui se
              rentabilise sur le candidat signé — et qui continue de payer pendant 24 mois
              sur les <strong>99 autres</strong> candidats qui sont passés par votre vivier.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — Chiffres avant / après */}
      <SectionBackground page="campagnes" section="campagnes-results" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Les résultats</span>

            <h2 className={h2Class}>
              Ce qu’une campagne de 6 mois change{' '}
              <span className={gradientText}>dans votre P&amp;L.</span>
            </h2>
          </div>

          <div className="max-w-5xl mx-auto rounded-[2rem] bg-[#fbfaf8] ring-1 ring-neutral-200 overflow-hidden">
            <div className="hidden md:grid grid-cols-[1.4fr_1fr_1fr] bg-white border-b border-neutral-200">
              <div className="p-5 text-xs font-semibold text-neutral-400 uppercase tracking-widest">
                Indicateur
              </div>
              <div className="p-5 text-xs font-semibold text-neutral-400 uppercase tracking-widest">
                Sans campagne
              </div>
              <div className="p-5 text-xs font-semibold text-neutral-400 uppercase tracking-widest bg-gradient-to-r from-[#fde7f2] to-[#fde2d3]">
                Avec recruteurs.io
              </div>
            </div>

            {beforeAfterRows.map((row, idx) => (
              <div
                key={row.label}
                className={`grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr] ${
                  idx !== beforeAfterRows.length - 1 ? 'border-b border-neutral-200' : ''
                }`}
              >
                <div className="p-5 md:p-6">
                  <p className="text-sm font-semibold text-neutral-500 uppercase tracking-wider md:hidden mb-1">
                    Indicateur
                  </p>
                  <p className="text-base font-semibold text-[#30475e]">{row.label}</p>
                </div>
                <div className="p-5 md:p-6 bg-white md:bg-transparent">
                  <p className="text-sm font-semibold text-neutral-400 uppercase tracking-wider md:hidden mb-1">
                    Sans campagne
                  </p>
                  <p className="text-base text-neutral-500">{row.before}</p>
                </div>
                <div className="p-5 md:p-6 bg-gradient-to-r from-[#fde7f2]/40 to-[#fde2d3]/40">
                  <p className="text-sm font-semibold text-[#d03484] uppercase tracking-wider md:hidden mb-1">
                    Avec recruteurs.io
                  </p>
                  <p className={`text-base font-semibold ${gradientText}`}>{row.after}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-sm text-neutral-400 text-center mt-8 max-w-2xl mx-auto">
            Fourchettes représentatives du marché. Les résultats réels dépendent du
            secteur, du volume de postes et de la maturité de votre marque employeur
            au démarrage.
          </p>
        </Section>
      </SectionBackground>

      {/* SECTION 6 bis — Témoignage candidat (preuve sociale Parcours scénarisé) */}
      <CandidateTestimonial
        eyebrow="Témoignage candidat"
        sectionTitle={
          <>
            Le vivier, vu{' '}
            <span className={gradientText}>du côté candidat.</span>
          </>
        }
        name="Benoit"
        role="Commercial"
        verified
        quote="Une **expérience candidat** vraiment bien pensée ! Toutes les informations que l'on cherche habituellement un peu partout sont **enfin accessibles au même endroit**. J'ai même reconnu une ancienne collègue dans la vidéo, ce qui a rendu l'**expérience encore plus engageante** !"
        background="white"
      />

      {/* SECTION 7 — Presse / vidéo */}
      <SectionBackground page="campagnes" section="campagnes-press" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>
              <PlayCircle className="w-4 h-4 mr-2" />
              Vu à la télé
            </span>

            <h2 className={h2Class}>
              Notre vision du recrutement,{' '}
              <span className={gradientText}>expliquée à l’antenne.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Notre équipe, notre méthode, notre conviction sur le recrutement
              moderne — racontées en quelques minutes.
            </p>
          </div>

          <div className="max-w-4xl mx-auto rounded-[2rem] overflow-hidden ring-1 ring-neutral-200 shadow-xl">
            <div
              className="relative w-full"
              style={{ paddingBottom: '56.25%', height: 0 }}
            >
              <iframe
                src="https://www.youtube.com/embed/sXY1BChmGSc"
                title="recruteurs.io passe à la télé — vision du recrutement"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="absolute top-0 left-0 w-full h-full"
              />
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* CTA FINAL */}
      <SectionBackground page="campagnes" section="campagnes-cta" className="bg-white">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Démarrons par un audit{' '}
              <span className={gradientText}>de votre marque employeur.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              30 minutes, sans engagement. Nous regardons ensemble votre culture,
              vos enjeux de recrutement et la matière disponible pour bâtir
              votre vivier.
            </p>

            <button
              onClick={() => navigate('/reservation')}
              className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Réserver l’audit (30 min)
            </button>
          </div>
        </Section>
      </SectionBackground>

      {/* Modale livre blanc — exit-intent / scroll 60% / fallback 30s, 1 fois max */}
      <WhitepaperModal
        open={whitepaper.isOpen}
        onClose={whitepaper.close}
        onDownloaded={whitepaper.markDownloaded}
        paper={ATTIRER_ONBOARDER_TALENTS}
      />
    </Layout>
  );
}
